import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Stack,
    Link,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ApplepayIcon, PaypalIcon, GiftIcon} from '../../../components/icons'
import {getCreditCardIcon, buildMaskedCardDisplay} from '../../../utils/cc-utils'

// CONSTANTS
import {CREDIT_CARD, PAYPAL, APPLE_PAY, GIFT_CARD} from '../constant'

const BillingAddress = ({street, city, postalCode}) => {
    return (
        <Stack alignItems="flex-start" spacing={1}>
            <Text fontWeight="bold" textTransform="uppercase" fontSize="sm" lineHeight="18px">
                <FormattedMessage
                    id="account_order_detail.label.billing_address"
                    defaultMessage="Billing Address"
                />
            </Text>
            <Text fontSize="sm" lineHeight="18px">{`${street}, ${city}, ${postalCode}`}</Text>
        </Stack>
    )
}

BillingAddress.propTypes = {
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string
}

const PaymentMethod = ({paymentMethod}) => {
    const payment = paymentMethod?.[0]
    const paymentType = payment?.paymentMethod
    const isCreditCard = paymentType?.toLowerCase() === CREDIT_CARD
    const CardIcon = getCreditCardIcon(payment?.name)
    const isPayPal = paymentType?.toLowerCase() === PAYPAL
    const isApplePay = paymentType?.toLowerCase() === APPLE_PAY
    const isGiftCard = paymentType?.toLowerCase() === GIFT_CARD

    // Credit Card Layout
    if (isCreditCard) {
        return (
            <Stack alignItems="flex-start" spacing={1} mb={4}>
                <Text fontWeight="bold" textTransform="uppercase" fontSize="sm" lineHeight="18px">
                    <FormattedMessage
                        id="account_order_detail.label.credit_card"
                        defaultMessage="Credit Card"
                    />
                </Text>
                <Flex alignItems="center" gap={6} fontSize="sm">
                    <Flex gap={3} alignItems="center">
                        <CardIcon w="42px" h="100%" />
                        <Text>{buildMaskedCardDisplay(payment.cardNumber.slice(-4), 4)}</Text>
                    </Flex>
                    {payment.expirationMonth && payment.expirationYear && (
                        <Text>
                            <FormattedMessage
                                id="account_order_detail.label.expires"
                                defaultMessage="{month}/{year}"
                                values={{
                                    month: String(payment.expirationMonth).padStart(2, '0'),
                                    year: payment.expirationYear
                                }}
                            />
                        </Text>
                    )}
                </Flex>
            </Stack>
        )
    }

    // PayPal Layout
    if (isPayPal) {
        return (
            <Stack alignItems="flex-start" spacing={2} mb={4}>
                <Text fontWeight="bold" textTransform="uppercase" fontSize="sm" lineHeight="18px">
                    <FormattedMessage
                        id="account_order_detail.label.paypal"
                        defaultMessage="PayPal"
                    />
                </Text>
                <Flex gap={3} fontSize="sm">
                    <PaypalIcon w="42px" />
                    {/* TODO: Define what is Subtext for PayPal */}
                    <Text>
                        <FormattedMessage
                            id="account_order_detail.paypal.subtext"
                            defaultMessage="You can view your complete PayPal transaction"
                        />{' '}
                        <Link href="https://paypal.com" textDecoration="underline">
                            <FormattedMessage
                                id="account_order_detail.paypal.link"
                                defaultMessage="here"
                            />
                        </Link>
                    </Text>
                </Flex>
            </Stack>
        )
    }

    // Apple Pay Layout
    if (isApplePay) {
        return (
            <Stack alignItems="flex-start" spacing={2} mb={4}>
                <Text fontWeight="bold" textTransform="uppercase" fontSize="sm" lineHeight="18px">
                    <FormattedMessage
                        id="account_order_detail.label.apple_pay"
                        defaultMessage="Apple Pay"
                    />
                </Text>
                <Flex gap={3} fontSize="sm">
                    <ApplepayIcon w="42px" />
                    {/* TODO: Define what is Subtext for Apple Pay */}
                    <Text>
                        <FormattedMessage
                            id="account_order_detail.applepay.subtext"
                            defaultMessage="Payment processed through Apple Pay"
                        />
                    </Text>
                </Flex>
            </Stack>
        )
    }

    if (isGiftCard) {
        return (
            <Stack alignItems="flex-start" spacing={1} mb={4}>
                <Text fontWeight="bold" textTransform="uppercase" fontSize="sm" lineHeight="18px">
                    <FormattedMessage
                        id="account_order_detail.label.gift_card"
                        defaultMessage="Gift Card"
                    />
                </Text>
                <Flex alignItems="center" gap={6} fontSize="sm">
                    <GiftIcon w="42px" />
                    <Text>{buildMaskedCardDisplay(payment.cardNumber.slice(-4), 4)}</Text>
                </Flex>
            </Stack>
        )
    }

    // Fallback
    return (
        <Text fontSize="sm" mb={4} lineHeight="18px">
            {payment?.name}
        </Text>
    )
}

PaymentMethod.propTypes = {
    paymentMethod: PropTypes.arrayOf(
        PropTypes.shape({
            paymentMethod: PropTypes.string,
            name: PropTypes.string,
            cardNumber: PropTypes.string,
            expirationMonth: PropTypes.string,
            expirationYear: PropTypes.string
        })
    ).isRequired
}

export const OrderPayment = ({order}) => {
    return (
        <Box>
            <Text fontSize="md" fontWeight={700} textTransform="uppercase" mb={4} lineHeight="20px">
                <FormattedMessage
                    defaultMessage="Payment"
                    id="account_order_detail.label.payment"
                />
            </Text>
            <PaymentMethod paymentMethod={order.payment} />
            <BillingAddress {...order.billingAddress} />
            <Divider display={{base: 'block', lg: 'none'}} mt={6} borderColor="grey.300" />
        </Box>
    )
}

OrderPayment.propTypes = {
    order: PropTypes.shape({
        payment: PropTypes.array.isRequired,
        billingAddress: PropTypes.shape({
            street: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            postalCode: PropTypes.string
        })
    }).isRequired
}
